/**
 * useCompanionActionMenu Hook
 * 
 * Manages the state of the companion action menu and item selection.
 * 
 * Responsibilities:
 * - Menu open/close state
 * - Selected action tracking
 * - Item resolution for selected action
 * - Click outside handling
 * - Route change cleanup
 * 
 * This hook is the single source of truth for menu state.
 * Components subscribe to this state and call actions to modify it.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useBlobbonautProfile } from '@/hooks/useBlobbonautProfile';
import { getLiveShopItems } from '@/blobbi/shop/lib/blobbi-shop-items';
import { canUseItemForStage } from '@/blobbi/actions/lib/blobbi-action-utils';

import type {
  CompanionMenuAction,
  CompanionMenuState,
  CompanionItem,
} from './types';
import {
  INITIAL_MENU_STATE,
  getItemCategoryForAction,
  MENU_ACTIONS,
} from './types';

interface UseCompanionActionMenuOptions {
  /** Whether the menu system should be active */
  isActive: boolean;
  /** Current companion's stage (affects which items are usable) */
  stage?: 'egg' | 'baby' | 'adult';
  /** Callback when menu opens */
  onOpen?: () => void;
  /** Callback when menu closes */
  onClose?: () => void;
  /** Callback when an action is selected */
  onActionSelect?: (action: CompanionMenuAction) => void;
  /** Callback when an item is clicked */
  onItemClick?: (item: CompanionItem) => void;
}

interface UseCompanionActionMenuResult {
  /** Current menu state */
  menuState: CompanionMenuState;
  /** Available menu actions (filtered by stage) */
  availableActions: typeof MENU_ACTIONS;
  /** Toggle menu open/close */
  toggleMenu: () => void;
  /** Open the menu */
  openMenu: () => void;
  /** Close the menu and clear selection */
  closeMenu: () => void;
  /** Select an action (shows its items) */
  selectAction: (action: CompanionMenuAction) => void;
  /** Clear the selected action (hides items) */
  clearAction: () => void;
  /** Handle item click */
  handleItemClick: (item: CompanionItem) => void;
}

/**
 * Resolve available items for a specific action/category from the shop catalog.
 * 
 * Items are sourced from the full shop catalog — all items are
 * available as reusable abilities/tools, filtered only by stage.
 * 
 * Uses the centralized `canUseItemForStage` function to ensure consistent
 * stage-based filtering across all UIs:
 * - Egg-only items (like Shell Repair Kit) are excluded for baby/adult companions
 * - Baby/adult-only items (food, toys) are excluded for eggs
 * - Items without relevant effects are excluded
 * 
 * Since companions can only be baby or adult (not egg), this effectively
 * filters out all egg-only items from the companion interaction system.
 */
function resolveItemsForAction(
  action: CompanionMenuAction,
  stage: 'egg' | 'baby' | 'adult'
): CompanionItem[] {
  const category = getItemCategoryForAction(action);
  
  // Sleep action has no items
  if (!category) return [];
  
  const allItems = getLiveShopItems();
  const items: CompanionItem[] = [];
  
  for (const shopItem of allItems) {
    if (shopItem.type !== category) continue;
    
    // Use centralized stage-based filtering
    // This handles:
    // - Shell Repair Kit: only for eggs (excluded for baby/adult companions)
    // - Food/Toys: only for baby/adult (excluded for eggs)
    // - Medicine: must have health effect
    // - Hygiene: must have hygiene or happiness effect
    const usability = canUseItemForStage(shopItem.id, stage);
    if (!usability.canUse) {
      continue;
    }
    
    items.push({
      id: shopItem.id,
      name: shopItem.name,
      emoji: shopItem.icon,
      category: shopItem.type,
      quantity: Infinity,
      effect: shopItem.effect,
    });
  }
  
  return items;
}

/**
 * Hook to manage the companion action menu state.
 */
export function useCompanionActionMenu({
  isActive,
  stage = 'baby',
  onOpen,
  onClose,
  onActionSelect,
  onItemClick,
}: UseCompanionActionMenuOptions): UseCompanionActionMenuResult {
  const location = useLocation();
  const { profile } = useBlobbonautProfile();
  
  const [menuState, setMenuState] = useState<CompanionMenuState>(INITIAL_MENU_STATE);
  
  // Filter available actions based on stage
  const availableActions = useMemo(() => {
    if (stage === 'egg') {
      // Eggs can only use medicine and clean
      return MENU_ACTIONS.filter(a => 
        a.id === 'medicine' || a.id === 'clean'
      );
    }
    return MENU_ACTIONS;
  }, [stage]);
  
  /**
   * Close the menu and reset state.
   */
  const closeMenu = useCallback(() => {
    setMenuState(INITIAL_MENU_STATE);
    onClose?.();
  }, [onClose]);
  
  /**
   * Open the menu.
   */
  const openMenu = useCallback(() => {
    if (!isActive) return;
    
    setMenuState(prev => ({
      ...prev,
      isOpen: true,
    }));
    onOpen?.();
  }, [isActive, onOpen]);
  
  /**
   * Toggle menu open/close.
   */
  const toggleMenu = useCallback(() => {
    if (menuState.isOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [menuState.isOpen, closeMenu, openMenu]);
  
  /**
   * Select an action and resolve its items.
   */
  const selectAction = useCallback((action: CompanionMenuAction) => {
    if (!isActive || !profile) return;
    
    // If same action is selected, toggle it off
    if (menuState.selectedAction === action) {
      setMenuState(prev => ({
        ...prev,
        selectedAction: null,
        items: [],
      }));
      return;
    }
    
    // Resolve items for this action from the catalog (not inventory)
    const items = resolveItemsForAction(action, stage);
    
    setMenuState(prev => ({
      ...prev,
      selectedAction: action,
      items,
    }));
    
    onActionSelect?.(action);
  }, [isActive, profile, stage, menuState.selectedAction, onActionSelect]);
  
  /**
   * Clear the selected action.
   */
  const clearAction = useCallback(() => {
    setMenuState(prev => ({
      ...prev,
      selectedAction: null,
      items: [],
    }));
  }, []);
  
  /**
   * Handle item click.
   */
  const handleItemClick = useCallback((item: CompanionItem) => {
    onItemClick?.(item);
  }, [onItemClick]);
  
  // Close menu on route change
  useEffect(() => {
    if (menuState.isOpen) {
      closeMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  
  // Close menu when companion becomes inactive
  useEffect(() => {
    if (!isActive && menuState.isOpen) {
      closeMenu();
    }
  }, [isActive, menuState.isOpen, closeMenu]);
  
  return {
    menuState,
    availableActions,
    toggleMenu,
    openMenu,
    closeMenu,
    selectAction,
    clearAction,
    handleItemClick,
  };
}
